import { useState, useEffect, lazy } from 'react';
import { Lazy } from '../Lazy/Lazy';
import { signOut } from '../../services/signOut';
import { storeService } from '../../services/store.service';

// Lazy load Appointments since this wrapper is already lazy-loaded
const Appointments = lazy(() => import('./Appointments'));

export default function AppointmentsWrapper() {
  const [user, setUser] = useState<any | null>(null);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);

  // Load user and store from localStorage
  useEffect(() => {
    const initData = async () => {
      // Load CMS user data instead of regular user data
      const { ensureCMSUser } = await import('../../services/auth.service');
      const cmsUser = await ensureCMSUser();
      if (cmsUser) {
        // Transform CMS user data to the format expected by TopNavigation
        const transformedUser = {
          name: `${cmsUser.firstName || ''} ${cmsUser.lastName || ''}`.trim(),
          email: cmsUser.email || '',
          picture: cmsUser.picture || ''
        };
        setUser(transformedUser);
      }

      // Load company logo from cache, or fetch if not cached
      let logoUrl = storeService.getCompanyLogoUrl();
      if (!logoUrl) {
        // Fetch from API if not in cache
        try {
          const { getCurrentDomain } = await import('../../utils/domain');
          const { CMS_BASE_URL, API_CONFIG, CMS_ENVIRONMENT_QUERY } = await import('../../config/api.config');
          const response = await fetch(
            `${CMS_BASE_URL}/api/companies/by-domain-name?domainName=${getCurrentDomain()}${CMS_ENVIRONMENT_QUERY}`,
            { method: 'GET', headers: API_CONFIG.headers }
          );
          if (response.ok) {
            const data = await response.json();
            logoUrl = data.companyPublicLogoUrl || null;
            if (logoUrl) {
              localStorage.setItem('company_logo_cache', logoUrl);
            }
          }
        } catch (error) {
          console.error('Error fetching company logo:', error);
        }
      }
      if (logoUrl) {
        setCompanyLogoUrl(logoUrl);
      }

      // Load store from localStorage or default to first store
      const storedStore = localStorage.getItem('selectedStore');
      if (storedStore) {
        try {
          setSelectedStore(JSON.parse(storedStore));
        } catch (e) {
          console.error('Error parsing stored store:', e);
        }
      } else {
        // No stored store: the first one, for this page only. It is not saved
        // as the client's choice (G49): saving it sent every client who opened
        // My bookings first to that location from Book now, never asked.
        try {
          const { fetchStores } = await import('../../services/api');
          const stores = await fetchStores();
          if (stores && stores.length > 0) {
            setSelectedStore(stores[0]);
          }
        } catch (error) {
          console.error('Error fetching stores:', error);
        }
      }
    };

    initData();
  }, []);

  const handleLogin = (profile: any) => {
    setUser(profile);
    // Store in CMS user format
    localStorage.setItem('cms_user', JSON.stringify(profile));
  };

  const handleLogout = () => {
    signOut();
    setUser(null);
  };

  const handleLocationSelect = (store: any) => {
    setSelectedStore(store);
    localStorage.setItem('selectedStore', JSON.stringify(store));
  };

  return (
    <Lazy>
      <Appointments
        onClose={() => window.history.back()}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onLoginError={(error) => console.error('Login error:', error)}
        selectedStore={selectedStore}
        onLocationSelect={handleLocationSelect}
        companyLogoUrl={companyLogoUrl}
      />
    </Lazy>
  );
}
